import { type Message, updateQueryData } from '@/features/messages';
import { type InfiniteData, type UseInfiniteQueryResult, useQueryClient } from '@tanstack/react-query';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useEffect } from 'react';
import type { MessagesQueryParams } from '../models/message-pagination';
import { messageListKeys } from '../queries/message-list-keys';
import { MESSAGES_PAGE_SIZE } from '../queries/use-message-list';
import { useActionStackStore } from '../stores/action-stack';
import { highlightMessage } from '../utils/highlight-message';

export interface UseActionWorkerParams {
  conversationId?: Nullable<string>;
  messages: Message[];
  query: UseInfiniteQueryResult<InfiniteData<Message[], unknown>, Error>;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
}

/**
 * Drives queued actions for the message list (pagination, jumps, merges) in a single effect runner.
 * Ensures actions execute sequentially and restores scroll position when needed.
 */
export function useActionWorker({ conversationId, messages, query, virtualizer }: UseActionWorkerParams) {
  const queryClient = useQueryClient();
  const { consumeNext, completeAction, inFlight, queue, dispatchAction } = useActionStackStore();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (inFlight || !conversationId || messages.length === 0) return;

      const action = consumeNext(conversationId);
      if (!action) return;
      console.info('[ActionWorker] Action:', action);

      try {
        switch (action.type) {
          case 'fetchTop': {
            if (query.hasPreviousPage && !query.isFetchingPreviousPage) {
              const targetId = messages.at(0)?.messageId;
              await query.fetchPreviousPage();
              if (targetId) {
                dispatchAction({ type: 'restore', conversationId: conversationId!, targetMessageId: targetId });
              }
            }
            break;
          }
          case 'fetchBottom': {
            if (query.hasNextPage && !query.isFetchingNextPage) {
              const targetId = messages.at(-1)?.messageId;
              await query.fetchNextPage();
              if (targetId) {
                dispatchAction({ type: 'restore', conversationId: conversationId!, targetMessageId: targetId });
              }
            }
            break;
          }
          case 'jumpTo': {
            if (!conversationId) return;
            const idx = messages.findIndex((m) => m.messageId === action.targetMessageId);
            if (idx >= 0) {
              virtualizer.scrollToIndex(idx, { behavior: 'auto', align: 'center' });
            } else {
              queryClient.setQueryData<InfiniteData<Message>>(messageListKeys.conversation(conversationId!), {
                pages: [],
                pageParams: [
                  {
                    fromMessageId: action.targetMessageId,
                    direction: 'OLDER',
                    reset: true,
                    pageSize: MESSAGES_PAGE_SIZE
                  } as MessagesQueryParams
                ]
              });
              await queryClient.refetchQueries({ queryKey: messageListKeys.conversation(conversationId) });
              dispatchAction(action);
            }
            highlightMessage(conversationId, action.targetMessageId);
            break;
          }
          case 'insertMessage': {
            await updateQueryData(queryClient, conversationId!, action.message);
            break;
          }
          case 'scrollToBottom': {
            if (messages.length > 0) {
              virtualizer.scrollToIndex(messages.length - 1);
            }
            break;
          }
          case 'restore': {
            const targetIdx = messages.findIndex((m) => m.messageId === action.targetMessageId);
            if (targetIdx >= 0) {
              virtualizer.scrollToIndex(targetIdx);
            }
            break;
          }
          default:
            break;
        }
      } catch {
        // swallow errors to keep the worker running; upstream errors are non-fatal here
      } finally {
        completeAction();
        if (!cancelled) {
          queueMicrotask(run);
        }
      }
    };

    queueMicrotask(run);
    return () => {
      cancelled = true;
    };
  }, [
    dispatchAction,
    inFlight,
    queue,
    consumeNext,
    completeAction,
    query,
    queryClient,
    conversationId,
    messages.length,
    virtualizer
  ]);
}
